import dayjs from "dayjs";
import React from "react";
import ReactDOM from "react-dom";
import { loopDates } from "../util/date";
import Config from "../util/config";

interface MonthSelectProps {
  month: string;
  monthsToDisplay: number;
  calendarsToDisplay?: number;
  onChange: (month: string) => void;
}

const MonthSelect = (props: MonthSelectProps) => {
  const { month, calendarsToDisplay } = props;
  const start = dayjs();
  const end = start.clone().add(props.monthsToDisplay, "months");
  const options: JSX.Element[] = [];
  const selectOptions: JSX.Element[] = [];
  const allMonthsRef = React.useRef<HTMLDivElement>(null);
  const useAlternativeMonthDisplay =
    Config.get("alternativeMonthDisplay", "no") == "yes";

  loopDates(start, end, "month", (day: dayjs.Dayjs) => {
    options.push(generateMonthOption(day));
    selectOptions.push(generateMonthOption(day, true));
  });

  function generateMonthOption(
    day: dayjs.Dayjs,
    generateAsOptions: boolean = false,
  ) {
    const handleMonthSelection = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      props.onChange((event.target as HTMLElement).dataset.month);
    };

    if (!generateAsOptions) {
      return (
        <div key={day.format("YYYY-MM")} data-month={day.format("YYYY-MM")} onClick={handleMonthSelection}>
          {day.format("MMMM YYYY")}
        </div>
      );
    }
    return (
      <option key={day.format("YYYY-MM")} value={day.format("YYYY-MM")}>
        {day.format("MMMM YYYY")}
      </option>
    );
  }

  const onChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    props.onChange(evt.target.value);
  };

  const nextMonth = () => {
    const current = dayjs(month, "YYYY-MM");
    const nextMonth = current.clone().add(1, "month");

    props.onChange(nextMonth.format("YYYY-MM"));
  };

  const prevMonth = () => {
    const current = dayjs(month, "YYYY-MM");
    const prevMonth = current.clone().subtract(1, "month");

    if (
      dayjs(prevMonth).isSame(dayjs(new Date()), "month") ||
      dayjs(prevMonth).isAfter(dayjs(new Date()), "month")
    ) {
      props.onChange(prevMonth.format("YYYY-MM"));
    }
  };

  const monthDisplay = () => {
    const displayMonths: JSX.Element[] = [];
    const format = useAlternativeMonthDisplay ? "MMMM" : "MMMM YYYY";

    if (calendarsToDisplay >= 2) {
      Array(calendarsToDisplay)
        .fill(0)
        .map((_, i) => {
          displayMonths.push(
            <div key={i} className="month-selector--individual">
              <p>{dayjs(month).add(i, "months").format(format)}</p>
            </div>,
          );
        });

      return displayMonths;
    } else {
      return (
        <select
          className="month-selector__select"
          value={month}
          onChange={onChange}
        >
          {selectOptions}
        </select>
      );
    }
  };

  React.useEffect(() => {
    function handleClickOutside(evt: any) {
      const allMonths = allMonthsRef.current;
      if (
        useAlternativeMonthDisplay &&
        !allMonths.classList.contains("hidden") &&
        allMonths &&
        !allMonths.contains(evt.target)
      ) {
        allMonths.classList.add("hidden");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [allMonthsRef]);
  const alternativeMonthDisplay = () => {
    const handleMonthSelectorToggle = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      const allMonths = allMonthsRef.current;
      if (!allMonths) {
        console.error("All months element not detected");
        return;
      }
      allMonths.classList.toggle("hidden");
    };

    return (
      <>
        <select
          className="month-selector__select mobile-month-selector"
          value={month}
          onChange={onChange}
        >
          {selectOptions}
        </select>
        <div
          className="monthSelectorDropdown desktop-month-selector"
          onClick={handleMonthSelectorToggle}
        >
          <div className="currentMonth">
            <span className="monthSelectorText">
              {dayjs(month).format("MMMM YYYY")}
            </span>
            <i className="fa-solid fa-chevron-down"></i>
          </div>
          <div className="allMonths hidden" ref={allMonthsRef}>
            {options}
          </div>
        </div>
      </>
    );
  };

  if (useAlternativeMonthDisplay) {
    return (
      <div className="alternative-month-selector month-selector">
        <div className="month-changer month-selector">
          <span
            className="month-selector__arrow month-selector__arrow--prev"
            onClick={prevMonth}
            dangerouslySetInnerHTML={{
              __html: Config.get("monthArrowLeft", "<"),
            }}
          />

          {alternativeMonthDisplay()}

          <span
            className="month-selector__arrow month-selector__arrow--prev"
            onClick={nextMonth}
            dangerouslySetInnerHTML={{
              __html: Config.get("monthArrowRight", ">"),
            }}
          />
        </div>
        <div className="month-display">{monthDisplay()}</div>
      </div>
    );
  }

  return (
    <div className="month-selector">
      <span
        className="month-selector__arrow month-selector__arrow--prev"
        onClick={prevMonth}
        dangerouslySetInnerHTML={{ __html: Config.get("monthArrowLeft", "<") }}
      />

      {monthDisplay()}

      <span
        className="month-selector__arrow month-selector__arrow--prev"
        onClick={nextMonth}
        dangerouslySetInnerHTML={{ __html: Config.get("monthArrowRight", ">") }}
      />
    </div>
  );
};

export default MonthSelect;
